import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { updateTask, uploadFiles } from 'api/task.service';
import { useAppLocation } from 'hooks/helpers';
import { useToast } from 'hooks/ui';
import { Task } from 'models/task.model';
import { taskRoutes } from 'router/taskRoutes';
import { useApplicationStore } from 'store/application';
import { setDraftTask } from 'store/task';
import { taskKeys } from './queryKeys';

export const useTaskUpdateQuery = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { setLoading, removeLoading } = useApplicationStore();
  const { isCreate } = useAppLocation(taskRoutes);

  const { isPending, mutate } = useMutation({
    mutationFn: ({
      task,
      assignedGroup,
    }: {
      task: Task;
      assignedGroup: Task['taskGroupId'];
    }) => {
      setLoading();

      return updateTask(task.id, {
        title: task.title,
        description: task.description,
        errorMessage: task.errorMessage,
        successMessage: task.successMessage,
        comment: task.comment,
        validator: task.validator,
        workModels: task.workModels,
        answers: task.answers,
        taskGroupId: assignedGroup || task.taskGroupId,
        order: task.order,
        attachments: task.attachments?.filter((f) => f.id),
      }).then(async (res) => {
        const attachments = task.attachments?.filter(
          (attachment) => !attachment.id,
        );

        if (attachments?.length) {
          await uploadFiles(res.id, attachments);
        }

        return res;
      });
    },
    onSuccess: (task) => {
      toast.success('Task has been drafted successfully');

      if (!isCreate) {
        setDraftTask({ task });
      }
    },
    onError: (error: AxiosError) => toast.error(error.message),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
      removeLoading();
    },
  });

  return { isLoading: isPending, mutate };
};
